import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'bac-assp-bloc4';
const theme = { primary: '#059669', secondary: '#10B981', bg: '#ECFDF5', text: '#1a2e35' };
const titleImage = "https://static.prod-images.emergentagent.com/jobs/5ffa2715-dbd5-4a4a-b5be-ca1657b7c30e/images/2388986ed1df556347762a93787e05cc9b04ed60fe7bd8d4af29131dd85d0f5a.png";
const bgImage = "https://static.prod-images.emergentagent.com/jobs/5ffa2715-dbd5-4a4a-b5be-ca1657b7c30e/images/2388986ed1df556347762a93787e05cc9b04ed60fe7bd8d4af29131dd85d0f5a.png";

const slides = [
  // ===== TITRE =====
  { type: 'title', image: titleImage, blocTitle: "BLOC 4 – RÉALISER DES ACTIONS D'ÉDUCATION À LA SANTÉ" },

  // ===== SOMMAIRE =====
  { type: 'sommaire', sections: [
    { num: "1", title: "Les fondements de l'éducation à la santé", target: 8 },
    { num: "2", title: "Identifier les besoins d'un public", target: 28 },
    { num: "3", title: "Concevoir une action d'éducation à la santé", target: 41 },
    { num: "4", title: "Mettre en œuvre une action d'éducation à la santé", target: 57 },
    { num: "5", title: "Les supports et outils d'éducation à la santé", target: 71 },
    { num: "6", title: "Exemples d'actions d'éducation à la santé", target: 83 },
    { num: "7", title: "Évaluer une action d'éducation à la santé", target: 104 },
    { num: "8", title: "Attendus professionnels et VAE", target: 117 },
  ]},

  // ===== INTRODUCTION =====
  { type: 'subsection', title: "Introduction" },
  { type: 'richtext', title: "L'éducation à la santé", blocks: [
    { text: "L'éducation à la santé occupe une place essentielle dans l'accompagnement des personnes. Elle ne consiste pas uniquement à transmettre des informations ou à donner des conseils. Elle vise à permettre à une personne ou à un groupe de mieux comprendre les facteurs qui influencent sa santé, d'adopter des comportements favorables à son bien-être et de devenir progressivement acteur de sa propre santé." },
  ]},
  { type: 'richtext', title: "Le rôle du professionnel ASSP", blocks: [
    { text: "Dans le cadre du Bac Pro ASSP, le professionnel doit être capable de repérer des besoins, de concevoir une action d'éducation à la santé adaptée à un public, de la mettre en œuvre et d'en évaluer les effets. Cette compétence suppose une bonne connaissance des publics, des messages de prévention, des méthodes d'animation et des outils de communication." },
  ]},
  { type: 'richtext', title: "Thématiques variées", blocks: [
    { text: "L'éducation à la santé peut concerner des thématiques très variées :", list: [
      "Hygiène des mains, alimentation, sommeil",
      "Prévention des chutes, santé bucco-dentaire, hydratation",
      "Écrans, vaccination, activité physique",
      "Prévention des accidents domestiques et des infections",
      "Prévention des conduites à risque",
      "Soutien à l'autonomie dans les gestes du quotidien",
    ]},
  ]},
  { type: 'richtext', title: "Une logique éducative", blocks: [
    { text: "Le professionnel du Bac Pro ASSP intervient dans une logique éducative, sans jugement, dans le respect des capacités, des représentations, des habitudes et du contexte de vie des personnes accompagnées." },
    { note: "L'éducation à la santé ne consiste jamais à imposer. Il s'agit d'accompagner chaque personne vers une meilleure compréhension de sa santé, à son rythme." },
  ]},

  // =============================================
  // CHAPITRE 1 : Les fondements de l'éducation à la santé
  // =============================================
  { type: 'section', section: "1", title: "Les fondements de l'éducation à la santé" },

  // --- 1.1 Définition ---
  { type: 'subsection', title: "1.1 Définition de la santé, de la prévention et de l'éducation à la santé" },
  { type: 'richtext', title: "La santé : une notion globale", blocks: [
    { text: "La santé ne se résume pas à l'absence de maladie. Elle correspond à un état de bien-être physique, mental et social. Elle dépend de nombreux facteurs et se construit tout au long de la vie." },
  ]},
  { type: 'richtext', title: "Prévention et éducation à la santé", blocks: [
    { text: "La prévention regroupe l'ensemble des mesures destinées à éviter l'apparition d'un problème de santé, à le repérer précocement ou à limiter ses conséquences." },
    { text: "L'éducation à la santé s'inscrit dans cette dynamique. Elle vise à permettre à chacun de mieux comprendre sa santé et d'adopter des comportements favorables à son bien-être." },
  ]},
  { type: 'richtext', title: "Une démarche éducative", blocks: [
    { text: "Dans le secteur sanitaire et social, l'éducation à la santé est une démarche éducative qui cherche à rendre la personne actrice, dans la mesure de ses capacités, de sa santé et de sa qualité de vie." },
    { note: "L'objectif n'est pas d'imposer, mais d'accompagner chaque personne vers une meilleure compréhension de sa santé." },
  ]},

  // --- 1.2 Différences entre les notions ---
  { type: 'subsection', title: "1.2 Information, prévention, promotion et éducation à la santé" },
  { type: 'richtext', title: "Information et prévention", blocks: [
    { text: "Il est important de distinguer plusieurs notions proches, mais différentes." },
    { text: "L'information consiste à transmettre un message ou une donnée à une personne ou à un groupe. Par exemple, expliquer que le lavage des mains limite la transmission des microbes." },
    { text: "La prévention vise à réduire un risque ou à éviter un problème de santé. Par exemple, proposer une campagne de vaccination ou des conseils pour prévenir les chutes." },
  ]},
  { type: 'richtext', title: "Promotion de la santé", blocks: [
    { text: "La promotion de la santé a une approche plus globale. Elle vise à renforcer les capacités des personnes et des communautés à agir sur les facteurs qui influencent leur santé." },
  ]},
  { type: 'richtext', title: "L'éducation à la santé", blocks: [
    { text: "L'éducation à la santé se situe à l'intersection de ces approches. Elle ne consiste pas seulement à dire ce qu'il faut faire. Elle cherche à :", list: [
      "Faire comprendre les enjeux de santé",
      "Faire participer activement la personne",
      "Développer les compétences de la personne",
      "Accompagner vers des choix éclairés",
    ]},
  ]},

  // --- 1.3 Les déterminants de santé ---
  { type: 'subsection', title: "1.3 Les déterminants de santé" },
  { type: 'richtext', title: "Les facteurs qui influencent la santé", blocks: [
    { text: "La santé d'une personne est influencée par de nombreux déterminants :", list: [
      "Biologiques : âge, sexe, antécédents médicaux",
      "Psychologiques : estime de soi, stress, santé mentale",
      "Sociaux : revenus, logement, entourage, éducation, accès aux soins",
      "Environnementaux : pollution, bruit, sécurité, accessibilité, qualité de l'habitat",
    ]},
  ]},
  { type: 'richtext', title: "Prendre en compte les déterminants", blocks: [
    { text: "Dans une action d'éducation à la santé, le professionnel doit prendre en compte ces déterminants. On ne peut pas transmettre un message de prévention de la même manière à un enfant, à une personne âgée isolée, à une famille en difficulté sociale ou à une personne en situation de handicap." },
    { note: "Adapter le message au contexte de vie de la personne est une condition essentielle de l'efficacité de l'action." },
  ]},

  // --- 1.4 Les niveaux de prévention ---
  { type: 'subsection', title: "1.4 Les niveaux de prévention" },
  { type: 'richtext', title: "Prévention primaire", blocks: [
    { text: "AVANT le problème — Empêcher l'apparition d'un problème de santé." },
    { text: "Exemple : apprendre aux enfants à se laver les mains pour limiter la transmission des infections." },
  ]},
  { type: 'richtext', title: "Prévention secondaire", blocks: [
    { text: "AU DÉBUT du problème — Repérer précocement un problème pour éviter son aggravation." },
    { text: "Exemple : sensibiliser au dépistage d'un trouble visuel ou d'une dénutrition." },
  ]},
  { type: 'richtext', title: "Prévention tertiaire", blocks: [
    { text: "APRÈS le problème — Limiter les complications, les rechutes ou l'aggravation d'une maladie installée." },
    { text: "Exemple : éduquer une personne diabétique à l'équilibre alimentaire ou accompagner une personne âgée dans la prévention des chutes après un premier épisode." },
  ]},
  { type: 'richtext', title: "Le rôle du professionnel ASSP", blocks: [
    { text: "Le professionnel du Bac Pro ASSP intervient surtout dans les actions de prévention primaire et secondaire, tout en participant parfois à des démarches de prévention tertiaire sous la responsabilité de l'équipe." },
  ]},

  // --- 1.5 Intérêt dans le secteur ASSP ---
  { type: 'subsection', title: "1.5 Intérêt de l'éducation à la santé dans le secteur ASSP" },
  { type: 'richtext', title: "Les finalités de l'éducation à la santé", blocks: [
    { text: "L'éducation à la santé a plusieurs finalités dans le secteur ASSP :", list: [
      "Améliorer les connaissances",
      "Prévenir certains risques",
      "Favoriser l'autonomie",
      "Renforcer l'adhésion aux soins",
      "Soutenir les capacités de la personne",
      "Améliorer sa qualité de vie",
    ]},
  ]},
  { type: 'richtext', title: "Une compétence essentielle", blocks: [
    { text: "L'éducation à la santé est particulièrement importante auprès des enfants, des adolescents, des personnes âgées, des personnes dépendantes, des personnes malades ou en situation de handicap." },
    { text: "Elle participe également au travail avec les familles, en particulier dans le champ de la petite enfance et de l'aide à domicile." },
    { note: "L'éducation à la santé est une compétence concrète et essentielle dans l'accompagnement quotidien." },
  ]},

  // =============================================
  // CHAPITRE 2 : Identifier les besoins d'un public
  // =============================================
  { type: 'section', section: "2", title: "Identifier les besoins d'un public" },

  // --- 2.1 Notion de public cible ---
  { type: 'subsection', title: "2.1 Notion de public cible" },
  { type: 'richtext', title: "Identifier le public cible", blocks: [
    { text: "Une action d'éducation à la santé ne s'adresse pas à un public abstrait. Elle vise un public cible clairement identifié. Il peut s'agir de :", list: [
      "Enfants d'une école maternelle",
      "Résidents d'EHPAD",
      "Adolescents en structure éducative",
      "Parents de jeunes enfants",
      "Personnes en situation de handicap",
      "Patients en rééducation",
      "Bénéficiaires d'un service à domicile",
    ]},
    { note: "Identifier le public cible est une étape fondamentale, car toute l'action devra être pensée en fonction de ses caractéristiques." },
  ]},

  // --- 2.2 Analyse du contexte ---
  { type: 'subsection', title: "2.2 Analyse du contexte" },
  { type: 'richtext', title: "Le contexte d'intervention", blocks: [
    { text: "Le contexte d'intervention influence fortement la manière de concevoir une action. Une animation menée en crèche ne se prépare pas comme une action en EHPAD ou dans un service à domicile." },
    { text: "Il faut prendre en compte :", list: [
      "Le lieu et le moment",
      "Les ressources disponibles et le matériel",
      "Les habitudes de la structure",
      "Les contraintes institutionnelles",
      "La durée possible et le nombre de participants",
      "Les objectifs globaux de l'établissement ou du service",
    ]},
    { note: "L'analyse du contexte permet d'éviter les actions trop théoriques, irréalistes ou inadaptées." },
  ]},

  // --- 2.3 Recueil des besoins ---
  { type: 'subsection', title: "2.3 Recueil des besoins" },
  { type: 'richtext', title: "Les sources du recueil", blocks: [
    { text: "Avant de construire une action, il est nécessaire de recueillir les besoins. Ce recueil peut se faire à partir de plusieurs sources :", list: [
      "Observations du quotidien",
      "Échanges avec les personnes accompagnées",
      "Retours des familles",
      "Observations de l'équipe",
      "Questionnaires simples, entretiens",
      "Données déjà disponibles dans la structure",
      "Projets d'établissement ou constats répétés sur le terrain",
    ]},
  ]},
  { type: 'richtext', title: "Exemples de besoins identifiés", blocks: [
    { text: "Voici quelques exemples concrets :", list: [
      "Nombreuses infections hivernales chez les jeunes enfants → besoin sur l'hygiène des mains",
      "Plusieurs résidents présentent un risque de déshydratation → action sur l'hydratation",
      "Enfants consommant beaucoup d'écrans → travail de sensibilisation avec les familles",
    ]},
  ]},

  // --- 2.4 Prise en compte des caractéristiques du public ---
  { type: 'subsection', title: "2.4 Prise en compte des caractéristiques du public" },
  { type: 'richtext', title: "Adapter l'action au public", blocks: [
    { text: "Le professionnel doit adapter son action à :", list: [
      "L'âge et le niveau de compréhension",
      "Les habitudes et les capacités d'attention",
      "L'autonomie et le langage",
      "La culture et les croyances",
      "Le niveau de santé du public",
    ]},
  ]},
  { type: 'richtext', title: "Exemples d'adaptation selon le public", blocks: [
    { text: "Avec de jeunes enfants : on privilégiera les approches concrètes, visuelles, ludiques et courtes." },
    { text: "Avec des personnes âgées : il faudra tenir compte de la fatigabilité, de la mémoire, de l'audition, de la vue et du rythme." },
    { text: "Avec des personnes en situation de handicap : il pourra être nécessaire d'utiliser des supports simplifiés, sensoriels ou très visuels." },
    { note: "Cette adaptation est indispensable pour rendre l'action accessible et réellement utile." },
  ]},

  // --- 2.5 Hiérarchisation des besoins ---
  { type: 'subsection', title: "2.5 Hiérarchisation des besoins et choix d'une thématique" },
  { type: 'richtext', title: "Choisir une thématique", blocks: [
    { text: "Tous les besoins identifiés ne peuvent pas toujours être traités en même temps. Il faut donc les hiérarchiser. Le professionnel choisit une thématique en fonction de :", list: [
      "Son importance",
      "Sa fréquence",
      "Sa faisabilité",
      "Les attentes du public",
      "Les ressources disponibles",
    ]},
    { note: "Le choix doit être justifié. Il ne s'agit pas de choisir un thème au hasard, mais en lien avec un besoin réel et repéré." },
  ]},

  // =============================================
  // CHAPITRE 3 : Concevoir une action d'éducation à la santé
  // =============================================
  { type: 'section', section: "3", title: "Concevoir une action d'éducation à la santé" },

  // --- 3.1 Définition des objectifs ---
  { type: 'subsection', title: "3.1 Définition des objectifs" },
  { type: 'richtext', title: "Objectif général et objectifs opérationnels", blocks: [
    { text: "Une fois le besoin repéré, il est nécessaire de définir des objectifs. Les objectifs indiquent ce que l'action cherche à produire. Ils doivent être clairs, réalistes, mesurables si possible et adaptés au public." },
    { text: "On distingue souvent :", list: [
      "L'objectif général : donne le sens global de l'action",
      "Les objectifs opérationnels : précisent ce que les participants devront comprendre, savoir faire ou retenir à l'issue de l'animation",
    ]},
  ]},
  { type: 'richtext', title: "Exemple concret", blocks: [
    { text: "Pour une action sur l'hygiène des mains en école maternelle :" },
    { text: "Objectif général : prévenir la transmission des microbes." },
    { text: "Objectif opérationnel : les enfants seront capables d'identifier les moments clés où il faut se laver les mains." },
  ]},

  // --- 3.2 Choix du thème ---
  { type: 'subsection', title: "3.2 Choix du thème et justification" },
  { type: 'richtext', title: "Justifier son choix de thème", blocks: [
    { text: "Le thème doit être en lien avec le besoin repéré et adapté au public. Il doit aussi être pertinent par rapport au projet de la structure ou au contexte." },
    { text: "Le professionnel doit être capable de justifier son choix :", list: [
      "Action sur les chutes en EHPAD → justifiée par la fréquence des accidents observés",
      "Action sur le sommeil chez les adolescents → justifiée par des signes de fatigue, de baisse de concentration ou d'usage excessif des écrans",
    ]},
  ]},

  // --- 3.3 Choix des messages ---
  { type: 'subsection', title: "3.3 Choix des messages de prévention" },
  { type: 'richtext', title: "Des messages simples et positifs", blocks: [
    { text: "Une action d'éducation à la santé repose sur quelques messages essentiels. Ceux-ci doivent être :", list: [
      "Simples et compréhensibles",
      "Adaptés au public",
      "Formulés de manière positive",
      "Peu nombreux (éviter de surcharger l'action)",
    ]},
    { note: "Un bon message est utile, concret et facilement mémorisable. Il doit pouvoir être repris par la personne après l'action." },
  ]},

  // --- 3.4 Recherche documentaire ---
  { type: 'subsection', title: "3.4 Recherche documentaire et fiabilité des sources" },
  { type: 'richtext', title: "S'appuyer sur des informations fiables", blocks: [
    { text: "Le professionnel doit s'appuyer sur des informations fiables. Il doit rechercher ses informations dans :", list: [
      "Des documents professionnels",
      "Des recommandations reconnues",
      "Des supports institutionnels",
      "Des ressources validées",
    ]},
    { text: "La recherche documentaire fait partie du travail de conception. Elle permet de sécuriser le contenu de l'action et d'appuyer les messages sur des bases solides." },
    { note: "Cette exigence est essentielle pour éviter de transmettre des messages inexacts ou approximatifs." },
  ]},

  // --- 3.5 Choix des supports ---
  { type: 'subsection', title: "3.5 Choix des supports et outils" },
  { type: 'richtext', title: "Les supports possibles", blocks: [
    { text: "Le choix des supports dépend du public, du thème, du temps disponible et des objectifs. Il peut s'agir de :", list: [
      "Affiches, images, pictogrammes",
      "Vidéos, démonstrations",
      "Jeux, mises en situation, quizz",
      "Maquettes, ateliers pratiques",
    ]},
    { note: "Les supports doivent être lisibles, attractifs, compréhensibles et adaptés au niveau du public. Ils ne remplacent pas l'animation, mais la soutiennent." },
  ]},

  // --- 3.6 Préparation matérielle ---
  { type: 'subsection', title: "3.6 Préparation matérielle et organisationnelle" },
  { type: 'richtext', title: "Préparer rigoureusement l'action", blocks: [
    { text: "Une action réussie suppose une préparation rigoureuse. Le professionnel doit prévoir :", list: [
      "Le lieu et la disposition de l'espace",
      "Le matériel nécessaire",
      "La durée de l'animation",
      "Le nombre de participants",
      "Les consignes de sécurité",
      "Les modalités d'accueil",
      "Les éventuels partenaires",
    ]},
    { note: "Cette préparation conditionne en grande partie la qualité de l'action." },
  ]},

  // --- 3.7 Partenariats ---
  { type: 'subsection', title: "3.7 Partenariats et personnes ressources" },
  { type: 'richtext', title: "S'appuyer sur des partenaires", blocks: [
    { text: "Dans certaines situations, l'action peut être renforcée par l'intervention d'un partenaire ou d'une personne ressource :", list: [
      "Infirmier, diététicien, psychologue",
      "Éducateur, professionnel de prévention",
      "Association spécialisée",
    ]},
    { text: "Le professionnel du Bac Pro ASSP doit savoir identifier ces ressources, comprendre leur intérêt et, selon le contexte, participer à la coordination de l'action avec eux." },
  ]},

  // =============================================
  // CHAPITRE 4 : Mettre en œuvre une action d'éducation à la santé
  // =============================================
  { type: 'section', section: "4", title: "Mettre en œuvre une action d'éducation à la santé" },

  // --- 4.1 Accueil du public ---
  { type: 'subsection', title: "4.1 Accueil du public" },
  { type: 'richtext', title: "L'accueil : première étape de l'action", blocks: [
    { text: "L'accueil marque le début de l'action. Il doit être rassurant, clair et adapté au public. Le professionnel présente :", list: [
      "Le cadre de l'action",
      "Les objectifs visés",
      "Le déroulement prévu",
      "Les règles de fonctionnement",
    ]},
    { note: "Un accueil de qualité favorise l'adhésion, la participation et la confiance." },
  ]},

  // --- 4.2 Posture éducative ---
  { type: 'subsection', title: "4.2 Posture éducative du professionnel" },
  { type: 'richtext', title: "Une posture bienveillante et respectueuse", blocks: [
    { text: "Dans une action d'éducation à la santé, le professionnel n'est pas dans une posture de jugement ou de contrainte. Il adopte une posture éducative fondée sur :", list: [
      "Le respect",
      "L'écoute",
      "La valorisation",
      "L'adaptation",
      "La bienveillance",
    ]},
    { note: "Il ne s'agit pas de culpabiliser les personnes, mais de les aider à comprendre, à réfléchir et à progresser." },
  ]},

  // --- 4.3 Techniques d'animation ---
  { type: 'subsection', title: "4.3 Techniques d'animation" },
  { type: 'richtext', title: "Les techniques mobilisables", blocks: [
    { text: "Plusieurs techniques d'animation peuvent être mobilisées :", list: [
      "Échanges oraux, brainstorming",
      "Démonstration, atelier pratique",
      "Jeu pédagogique, quizz",
      "Support visuel, lecture d'images",
      "Mises en situation, manipulation d'objets",
      "Travail en petit groupe",
    ]},
  ]},
  { type: 'richtext', title: "Adapter la technique au public", blocks: [
    { text: "Le choix dépend du public et des objectifs :", list: [
      "Petite enfance : le jeu et la démonstration sont souvent privilégiés",
      "EHPAD : les échanges, la manipulation et le rythme lent sont importants",
      "Adolescents : les approches interactives et participatives sont généralement plus efficaces",
    ]},
  ]},

  // --- 4.4 Conduite de séance ---
  { type: 'subsection', title: "4.4 Conduite de séance" },
  { type: 'richtext', title: "Une progression logique", blocks: [
    { text: "La séance doit suivre une progression logique :", list: [
      "Introduction : présentation du thème et des objectifs",
      "Développement : transmission des messages principaux",
      "Conclusion : synthèse ou mise en perspective",
    ]},
    { text: "Le professionnel doit donner des consignes claires, relancer la participation, reformuler, s'assurer que le groupe suit et adapter son rythme. Il doit garder en tête les objectifs de départ tout en restant souple." },
  ]},

  // --- 4.5 Adaptation en cours d'action ---
  { type: 'subsection', title: "4.5 Adaptation en cours d'action" },
  { type: 'richtext', title: "Savoir s'ajuster", blocks: [
    { text: "Une action ne se déroule jamais exactement comme prévu. Le professionnel doit être capable d'adapter sa manière d'animer en fonction de :", list: [
      "La fatigue du public",
      "Une incompréhension",
      "Un manque d'attention",
      "Une difficulté matérielle",
      "Une réaction imprévue",
    ]},
    { note: "Cette capacité d'ajustement est une compétence essentielle." },
  ]},

  // --- 4.6 Gestion du groupe ---
  { type: 'subsection', title: "4.6 Gestion du groupe et participation" },
  { type: 'richtext', title: "Animer et réguler le groupe", blocks: [
    { text: "Animer un groupe implique de :", list: [
      "Favoriser la participation",
      "Réguler la parole",
      "Valoriser les interventions",
      "Maintenir l'attention",
      "Préserver un climat respectueux",
    ]},
    { text: "Le professionnel doit savoir équilibrer les temps de parole, éviter qu'une personne monopolise l'échange et encourager les plus discrets. Il doit aussi savoir recentrer le groupe si nécessaire." },
  ]},

  // =============================================
  // CHAPITRE 5 : Les supports et outils d'éducation à la santé
  // =============================================
  { type: 'section', section: "5", title: "Les supports et outils d'éducation à la santé" },

  // --- 5.1 Supports écrits ---
  { type: 'subsection', title: "5.1 Supports écrits" },
  { type: 'richtext', title: "Les supports écrits", blocks: [
    { text: "Les supports écrits peuvent prendre la forme de :", list: [
      "Fiches et dépliants",
      "Affiches",
      "Livrets",
      "Tableaux simples",
    ]},
    { text: "Ils permettent de garder une trace des messages transmis. Ils doivent être lisibles, synthétiques, bien organisés et adaptés au niveau de lecture du public." },
  ]},

  // --- 5.2 Supports visuels et numériques ---
  { type: 'subsection', title: "5.2 Supports visuels et numériques" },
  { type: 'richtext', title: "Capter l'attention et faciliter la compréhension", blocks: [
    { text: "Les images, vidéos, pictogrammes, diaporamas ou outils numériques sont particulièrement utiles pour capter l'attention et faciliter la compréhension." },
    { note: "Ils sont précieux lorsque le public présente des difficultés de lecture ou a besoin de concret." },
  ]},

  // --- 5.3 Jeux, ateliers et démonstrations ---
  { type: 'subsection', title: "5.3 Jeux, ateliers et démonstrations" },
  { type: 'richtext', title: "Rendre l'action vivante", blocks: [
    { text: "Le jeu et la démonstration sont particulièrement efficaces pour rendre l'action vivante et accessible. Ils permettent :", list: [
      "D'impliquer les participants",
      "De rendre l'apprentissage plus concret",
      "D'éviter un format trop descendant",
    ]},
  ]},
  { type: 'richtext', title: "Exemples concrets", blocks: [
    { text: "Pour l'hygiène des mains : une démonstration avec un produit fluorescent peut permettre de visualiser les zones mal lavées." },
    { text: "Pour la nutrition : un tri d'images ou un atelier de composition d'un repas équilibré peut être très parlant." },
  ]},

  // --- 5.4 Outils adaptés selon l'âge ---
  { type: 'subsection', title: "5.4 Outils adaptés selon l'âge et le public" },
  { type: 'richtext', title: "Adapter les outils au public", blocks: [
    { text: "Le choix des outils doit toujours tenir compte de l'âge, des capacités et des particularités du public. Un enfant n'apprend pas comme une personne âgée. Une personne présentant un trouble cognitif ou un handicap sensoriel n'aura pas les mêmes besoins de support." },
    { note: "Le professionnel doit donc adapter, simplifier ou modifier ses supports si nécessaire." },
  ]},

  // --- 5.5 Critères de qualité ---
  { type: 'subsection', title: "5.5 Critères de qualité d'un support" },
  { type: 'richtext', title: "Les critères d'un bon support", blocks: [
    { text: "Un bon support doit être :", list: [
      "Compréhensible",
      "Attractif",
      "Pertinent",
      "Fiable",
      "Lisible",
      "Cohérent avec l'objectif de l'action",
    ]},
    { note: "Il doit aussi respecter la dignité du public et éviter les images stigmatisantes ou culpabilisantes." },
  ]},

  // =============================================
  // CHAPITRE 6 : Exemples d'actions d'éducation à la santé
  // =============================================
  { type: 'section', section: "6", title: "Exemples d'actions d'éducation à la santé" },

  // --- 6.1 Hygiène des mains ---
  { type: 'subsection', title: "6.1 Hygiène des mains" },
  { type: 'richtext', title: "Action sur l'hygiène des mains", blocks: [
    { text: "Cette action est particulièrement pertinente en petite enfance, en école, en structure collective ou en établissement médico-social." },
    { text: "Elle vise à faire comprendre l'intérêt du lavage des mains et à repérer les moments où il est nécessaire. Elle peut s'appuyer sur une démonstration, un rituel, un jeu ou une affiche." },
  ]},

  // --- 6.2 Équilibre alimentaire ---
  { type: 'subsection', title: "6.2 Équilibre alimentaire" },
  { type: 'richtext', title: "Action sur l'alimentation", blocks: [
    { text: "Une action sur l'alimentation peut viser à :", list: [
      "Faire connaître les grands repères nutritionnels",
      "Valoriser la variété alimentaire",
      "Sensibiliser à l'importance du petit-déjeuner",
      "Accompagner la prévention de la dénutrition",
    ]},
    { text: "Elle peut prendre la forme d'un atelier, d'un jeu de classement, d'un échange ou d'une activité pratique." },
  ]},

  // --- 6.3 Hydratation ---
  { type: 'subsection', title: "6.3 Hydratation" },
  { type: 'richtext', title: "L'hydratation chez la personne âgée", blocks: [
    { text: "Chez la personne âgée, l'hydratation est une thématique essentielle. L'action peut porter sur :", list: [
      "Les signes de déshydratation",
      "L'importance de boire régulièrement",
      "Les astuces pour varier les boissons",
      "Les moments favorables à la prise de boissons",
    ]},
  ]},

  // --- 6.4 Sommeil ---
  { type: 'subsection', title: "6.4 Sommeil et rythmes de vie" },
  { type: 'richtext', title: "Action sur le sommeil", blocks: [
    { text: "Cette thématique peut concerner les enfants, les adolescents, les adultes ou les personnes âgées. L'action peut porter sur :", list: [
      "Les rituels d'endormissement",
      "Les besoins en sommeil selon l'âge",
      "Les facteurs qui perturbent l'endormissement",
      "Les habitudes favorables à un meilleur repos",
    ]},
  ]},

  // --- 6.5 Prévention des chutes ---
  { type: 'subsection', title: "6.5 Prévention des chutes" },
  { type: 'richtext', title: "Prévenir les chutes", blocks: [
    { text: "En établissement pour personnes âgées ou à domicile, la prévention des chutes est une thématique fréquente. L'action peut porter sur :", list: [
      "L'aménagement du logement",
      "Le chaussage adapté",
      "Les aides techniques",
      "L'éclairage",
      "Les déplacements sécurisés",
      "L'activité physique adaptée",
    ]},
  ]},

  // --- 6.6 Santé bucco-dentaire ---
  { type: 'subsection', title: "6.6 Santé bucco-dentaire" },
  { type: 'richtext', title: "Promouvoir l'hygiène bucco-dentaire", blocks: [
    { text: "Cette action vise à promouvoir le brossage des dents, à faire comprendre son intérêt et à favoriser de bonnes habitudes." },
    { note: "Elle est souvent adaptée aux enfants, mais elle peut aussi concerner les personnes dépendantes ou âgées." },
  ]},

  // --- 6.7 Accidents domestiques ---
  { type: 'subsection', title: "6.7 Prévention des accidents domestiques" },
  { type: 'richtext', title: "Les risques du quotidien", blocks: [
    { text: "Chez les jeunes enfants, cette action peut concerner :", list: [
      "Les brûlures",
      "Les chutes",
      "Les intoxications",
      "Les noyades",
      "Les étouffements",
    ]},
    { text: "Avec les familles, elle permet de sensibiliser aux risques du quotidien et aux mesures de protection." },
  ]},

  // --- 6.8 Écrans et conduites à risque ---
  { type: 'subsection', title: "6.8 Prévention des écrans et des conduites à risque" },
  { type: 'richtext', title: "Chez les adolescents", blocks: [
    { text: "Chez les adolescents, l'éducation à la santé peut inclure :", list: [
      "Les écrans et le sommeil",
      "Les conduites à risque",
      "Les consommations",
      "La santé mentale",
      "La pression sociale",
    ]},
    { note: "L'approche doit être adaptée, non moralisatrice et participative." },
  ]},

  // --- 6.9 Activité physique ---
  { type: 'subsection', title: "6.9 Activité physique et bien-être" },
  { type: 'richtext', title: "Le mouvement au service de la santé", blocks: [
    { text: "Cette thématique peut être abordée avec tous les publics, en l'adaptant aux capacités de chacun. L'objectif est de montrer que le mouvement participe :", list: [
      "Au bien-être",
      "À l'autonomie",
      "À la prévention des maladies",
      "À la qualité de vie",
    ]},
  ]},

  // --- 6.10 Prévention adaptée ---
  { type: 'subsection', title: "6.10 Prévention adaptée selon le public" },
  { type: 'richtext', title: "Adapter l'action à chaque public", blocks: [
    { text: "Le professionnel doit toujours adapter ses actions au public :", list: [
      "Personne âgée : prendre en compte le rythme, la fatigabilité, la mémoire et le vécu",
      "Enfant : le jeu, l'image et la répétition sont essentiels",
      "Personne en situation de handicap : l'accessibilité des messages et des supports doit être particulièrement travaillée",
    ]},
  ]},

  // =============================================
  // CHAPITRE 7 : Évaluer une action d'éducation à la santé
  // =============================================
  { type: 'section', section: "7", title: "Évaluer une action d'éducation à la santé" },

  // --- 7.1 Intérêt de l'évaluation ---
  { type: 'subsection', title: "7.1 Intérêt de l'évaluation" },
  { type: 'richtext', title: "Pourquoi évaluer ?", blocks: [
    { text: "L'évaluation permet de :", list: [
      "Vérifier si l'action a répondu aux objectifs fixés",
      "Savoir si le public a compris les messages",
      "Vérifier si l'organisation était adaptée",
      "Améliorer les actions futures",
    ]},
  ]},

  // --- 7.2 Critères et indicateurs ---
  { type: 'subsection', title: "7.2 Critères et indicateurs d'évaluation" },
  { type: 'richtext', title: "Sur quoi porte l'évaluation ?", blocks: [
    { text: "L'évaluation peut porter sur plusieurs éléments :", list: [
      "Participation du public",
      "Compréhension des messages",
      "Intérêt suscité",
      "Qualité du support",
      "Déroulement de l'action",
      "Respect du temps",
      "Satisfaction des participants",
      "Changements observés à distance",
    ]},
  ]},

  // --- 7.3 Recueil des retours ---
  { type: 'subsection', title: "7.3 Recueil des retours du public" },
  { type: 'richtext', title: "Comment recueillir les retours ?", blocks: [
    { text: "Le professionnel peut recueillir les retours du public par :", list: [
      "Des échanges et des questions",
      "Un quizz",
      "Une observation des réactions",
      "Un questionnaire simple",
      "Un bilan oral",
    ]},
    { note: "Le mode de recueil doit être adapté au public." },
  ]},

  // --- 7.4 Analyse des résultats ---
  { type: 'subsection', title: "7.4 Analyse des résultats" },
  { type: 'richtext', title: "Analyser avec objectivité", blocks: [
    { text: "Les résultats de l'évaluation doivent être analysés avec objectivité. Le professionnel s'interroge sur :", list: [
      "Ce qui a fonctionné",
      "Ce qui a moins bien marché",
      "Ce qui a été compris ou non",
      "Les ajustements nécessaires",
    ]},
  ]},

  // --- 7.5 Ajustements ---
  { type: 'subsection', title: "7.5 Ajustements et amélioration" },
  { type: 'richtext', title: "Améliorer l'action", blocks: [
    { text: "L'évaluation ne sert pas uniquement à constater. Elle permet d'améliorer l'action :", list: [
      "Modifier un support",
      "Ajuster la durée",
      "Changer une méthode d'animation",
      "Simplifier un message",
      "Choisir un autre moment",
    ]},
  ]},

  // --- 7.6 Transmissions et traçabilité ---
  { type: 'subsection', title: "7.6 Transmissions, bilan et traçabilité" },
  { type: 'richtext', title: "Inscrire l'action dans la continuité", blocks: [
    { text: "Après l'action, le professionnel peut être amené à :", list: [
      "Transmettre les éléments importants à l'équipe",
      "Rédiger un bilan",
      "Compléter un document de suivi",
    ]},
    { note: "Cette traçabilité permet d'inscrire l'action dans une continuité professionnelle." },
  ]},

  // =============================================
  // CHAPITRE 8 : Attendus professionnels et VAE
  // =============================================
  { type: 'section', section: "8", title: "Attendus professionnels et VAE" },

  { type: 'richtext', title: "Ce que le jury attend", blocks: [
    { text: "Dans le cadre de la VAE, le jury attend que le candidat montre qu'il sait :", list: [
      "Analyser un besoin",
      "Choisir une thématique pertinente",
      "Construire une action adaptée",
      "L'animer avec une posture professionnelle juste",
      "En évaluer les effets",
    ]},
  ]},
  { type: 'richtext', title: "Aller au-delà de la description", blocks: [
    { text: "Il ne suffit pas de dire qu'on a « fait une animation ». Il faut être capable d'expliquer :", list: [
      "Pourquoi ce thème a été choisi",
      "Pour quel public",
      "Avec quels objectifs",
      "Quels outils et quelles adaptations",
      "Quels résultats obtenus",
    ]},
  ]},
  { type: 'richtext', title: "S'inscrire dans un contexte professionnel", blocks: [
    { text: "Le candidat doit également démontrer qu'il sait inscrire son action dans un contexte professionnel précis, en lien avec les besoins du public et le projet de la structure." },
    { note: "C'est un point fort du Bloc 4. Montrez que vous maîtrisez la démarche complète : du besoin à l'évaluation." },
  ]},

  // ===== CONCLUSION =====
  { type: 'richtext', title: "Conclusion du Bloc 4", blocks: [
    { text: "L'éducation à la santé fait pleinement partie du rôle du professionnel ASSP. Elle permet de prévenir, d'accompagner, de soutenir l'autonomie et de renforcer la qualité de vie des personnes." },
  ]},
  { type: 'richtext', title: "Une compétence complète", blocks: [
    { text: "Concevoir et mener une action d'éducation à la santé suppose de :", list: [
      "Comprendre le public",
      "Identifier les besoins",
      "Construire une intervention cohérente",
      "L'animer avec justesse",
      "L'évaluer",
    ]},
    { text: "Cette compétence mobilise à la fois des connaissances, une posture éducative et une capacité d'adaptation au terrain." },
  ]},

  // ===== FÉLICITATIONS =====
  { type: 'congratulations', title: "Félicitations !" },
];

const BACASSPBloc4Approche = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved?.viewedSlides) setViewedSlides(new Set(saved.viewedSlides));
  }, []);

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 800), voice: 'nova', speed: 1.1 }) });
      if (r.ok) { const d = await r.json(); if (d.audio_url || d.audio_base64) { if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(d.audio_url || `data:audio/mp3;base64,${d.audio_base64}`); audioRef.current.play(); setIsPlaying(true); audioRef.current.onended = () => setIsPlaying(false); } }
    } catch (e) { console.log('TTS unavailable'); }
    setIsLoading(false);
  };
  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); } };
  const getSlideText = (s) => { let t = s.title || ''; if (s.blocks) s.blocks.forEach(b => { if (b.heading) t += ' ' + b.heading; if (b.text) t += ' ' + b.text; if (b.list) b.list.forEach(li => t += ' ' + li); }); return t; };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (i) => { setCurrentSlide(i); setViewedSlides(prev => new Set([...prev, i])); markSlideViewed(ACTIVITY_ID, i, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex items-center justify-center h-full relative" style={{ background: theme.bg }}>
            <img src={slide.image} alt="Bloc 4" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            {slide.blocTitle && (
              <div className="absolute bottom-0 left-0 right-0 text-center" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '2rem 1rem 1.5rem' }}>
                <h1 className="text-white font-bold text-lg md:text-xl drop-shadow-lg">{slide.blocTitle}</h1>
              </div>
            )}
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(236,253,245,0.85)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #047857 100%)`, padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-4 flex items-center justify-center">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm" style={{ border: '1px solid #e0e0e0' }} onClick={() => goToSlide(s.target - 1)}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-sm leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(5,150,105,0.45)' }} />
            <div className="text-center px-10 relative z-10">
              <span className="text-white/80 text-2xl md:text-3xl font-bold mb-3 block tracking-wide">Chapitre {slide.section}</span>
              <h2 className="text-xl md:text-2xl font-semibold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(5,150,105,0.35)' }} />
            <div className="text-center px-10 relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'richtext':
        return (
          <div className="h-full overflow-auto flex items-center justify-center p-6" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-3">
                {slide.blocks.map((block, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm" style={{ border: `1px solid ${theme.primary}12` }}>
                    {block.heading && <h4 className="font-semibold mb-2 text-[15px]" style={{ color: theme.primary }}>{block.heading}</h4>}
                    {block.text && <p className="text-gray-700 text-[15px] leading-relaxed mb-2">{block.text}</p>}
                    {block.list && (
                      <div className="space-y-1.5">
                        {block.list.map((li, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                            <span className="text-gray-600 text-[15px] leading-relaxed">{li}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {block.note && <p className="text-[14px] italic mt-2.5 p-2.5 rounded-md" style={{ background: `${theme.primary}08`, borderLeft: `3px solid ${theme.primary}`, color: '#475569' }}>{block.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(5,150,105,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">Vous avez terminé la présentation du <strong>Bloc 4</strong> du BAC ASSP.</p>
              <p className="text-white text-base mb-6 leading-relaxed drop-shadow">Vous maîtrisez désormais les fondamentaux de l'éducation à la santé : fondements, analyse des besoins, conception, mise en œuvre, supports, exemples concrets, évaluation et attendus VAE.</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-base font-semibold mb-1" style={{ color: theme.primary }}>Prêt(e) à tester vos connaissances ?</p>
                <p className="text-sm text-gray-600">Rendez-vous dans le <strong>Quiz - Bloc 4</strong> pour évaluer votre maîtrise.</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="bac-assp-bloc4-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bloc4-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bloc4-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bloc4-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPBloc4Approche;
